/* eslint-disable react/jsx-props-no-spreading */
import { useForm } from '@folklore/forms';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAuthUpdatePassword } from '../../contexts/AuthContext';
import { useUrlGenerator } from '../../contexts/RoutesContext';
import SubmitButton from '../buttons/Submit';
import FormGroup from '../fields/FormGroup';
import TextField from '../fields/Text';
import ArrowIcon from '../icons/Arrow';

import styles from '../../../styles/forms/login.module.scss';

const propTypes = {
    email: PropTypes.string,
    token: PropTypes.string,
    onComplete: PropTypes.func,
};

const defaultProps = {
    email: null,
    token: null,
    onComplete: null,
};

const ResetPasswordForm = ({ email, token, onComplete }) => {
    const route = useUrlGenerator();
    const intl = useIntl();
    const { updatePassword, loading } = useAuthUpdatePassword();

    const postForm = useCallback((action, data) => updatePassword(data), [updatePassword]);
    const formValue = useMemo(() => ({ email, token }), [email, token]);
    const { fields, onSubmit } = useForm({
        fields: ['token', 'email', 'password', 'password_confirmation'],
        postForm,
        value: formValue,
        onComplete: onComplete !== null ? onComplete : () => {},
    });

    return (
        <form action={route('login')} onSubmit={onSubmit}>
            <FormGroup
                {...fields.email}
                label={<FormattedMessage defaultMessage="Email" description="Field label" />}
            >
                <TextField
                    {...fields.email}
                    type="text"
                    placeholder={intl.formatMessage({
                        defaultMessage: 'email@email.ca',
                        description: 'Field placeholder',
                    })}
                    required
                />
            </FormGroup>
            <FormGroup
                {...fields.password}
                label={<FormattedMessage defaultMessage="New password" description="Field label" />}
            >
                <TextField
                    {...fields.password}
                    type="password"
                    placeholder={intl.formatMessage({
                        defaultMessage: 'Choose a new password',
                        description: 'Field placeholder',
                    })}
                    required
                />
            </FormGroup>
            <FormGroup
                {...fields.password_confirmation}
                label={
                    <FormattedMessage
                        defaultMessage="Confirm new password"
                        description="Field label"
                    />
                }
            >
                <TextField
                    {...fields.password_confirmation}
                    type="password"
                    placeholder={intl.formatMessage({
                        defaultMessage: 'Confirm password',
                        description: 'Field placeholder',
                    })}
                    required
                />
            </FormGroup>
            <div className={styles.formActions}>
                <SubmitButton className={styles.formSubmitBtn} loading={loading}>
                    <FormattedMessage
                        defaultMessage="Change my password"
                        description="Button label"
                    />
                    <ArrowIcon />
                </SubmitButton>
            </div>
        </form>
    );
};
ResetPasswordForm.propTypes = propTypes;
ResetPasswordForm.defaultProps = defaultProps;

export default ResetPasswordForm;
